import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { Sprig } from "../components/ProductVisual";
import { formatINR, CLINIC } from "../data/catalog";
import { supabase } from "../lib/supabase";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const IN_STATES = [
  "Andaman and Nicobar Islands","Andhra Pradesh","Arunachal Pradesh","Assam","Bihar",
  "Chandigarh","Chhattisgarh","Dadra and Nagar Haveli and Daman and Diu","Delhi","Goa",
  "Gujarat","Haryana","Himachal Pradesh","Jammu and Kashmir","Jharkhand","Karnataka",
  "Kerala","Ladakh","Lakshadweep","Madhya Pradesh","Maharashtra","Manipur","Meghalaya",
  "Mizoram","Nagaland","Odisha","Puducherry","Punjab","Rajasthan","Sikkim","Tamil Nadu",
  "Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
];

const Field = ({ label, name, value, onChange, placeholder, type = "text", span = false, optional = false, error }) => (
  <label className={`block ${span ? "sm:col-span-2" : ""}`}>
    <span className="text-[11px] uppercase tracking-[0.22em] text-ink/55">
      {label} {optional && <span className="text-ink/35 normal-case tracking-normal">(optional)</span>}
    </span>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={`checkout-input-${name}`}
      data-field={name}
      aria-invalid={error ? "true" : "false"}
      className={`mt-2 w-full border bg-white/70 px-4 py-3.5 text-[15px] text-ink placeholder:text-ink/30 transition-colors ${
        error ? "border-[#B3261E] bg-[#FFF6F5]" : "hairline"
      }`}
    />
    {error && (
      <p className="mt-2 text-[12px] font-medium text-[#B3261E]" data-testid={`error-${name}`}>
        {error}
      </p>
    )}
  </label>
);

const StepHead = ({ n, title }) => (
  <div className="flex items-baseline gap-4">
    <span className="font-serif text-2xl italic text-sage-deep">{String(n).padStart(2, "0")}</span>
    <h2 className="font-serif text-2xl font-light">{title}</h2>
  </div>
);

const PAYMENT_METHODS = ["UPI", "Credit or debit card", "Net banking", "Wallets"];

const CheckoutPage = () => {
  const { items, subtotal, discount, delivery, total, coupon, clear, resolve } = useCart();
  const { user, orders, addOrder, saveAddress } = useAuth();
  const [form, setForm] = useState({
    email: user?.email || "",
    mobile: user?.phone || "",
    name: user?.name || "",
    addrMobile: user?.phone || "",
    pincode: "",
    flat: "",
    street: "",
    landmark: "",
    city: "",
    state: "",
    saveAddr: true,
  });
  const [payment, setPayment] = useState("UPI");
  const [errors, setErrors] = useState({});
  const [placed, setPlaced] = useState(null);
  const [busy, setBusy] = useState(false);
  const [payErr, setPayErr] = useState(null);
  const [formErr, setFormErr] = useState(null);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    setErrors((er) => ({ ...er, [name]: null }));
  };

  const validate = () => {
    const er = {};
    const need = (k) => (er[k] = "We need this one to get your order to you.");
    if (!EMAIL_RE.test(form.email.trim())) er.email = "That doesn't look like a valid email — please check it.";
    if (!/^[6-9]\d{9}$/.test(form.mobile.trim())) er.mobile = "That doesn't look like a valid mobile number — please check it.";
    if (!form.name.trim()) need("name");
    if (!/^[6-9]\d{9}$/.test(form.addrMobile.trim())) er.addrMobile = "That doesn't look like a valid mobile number — please check it.";
    if (!/^\d{6}$/.test(form.pincode.trim())) er.pincode = "That doesn't look like a valid pincode — please check it.";
    if (!form.flat.trim()) need("flat");
    if (!form.street.trim()) need("street");
    if (!form.city.trim()) need("city");
    if (!form.state.trim()) need("state");
    setErrors(er);

    const ORDER = ["email", "mobile", "name", "addrMobile", "pincode", "flat", "street", "city", "state"];
    const first = ORDER.find((k) => er[k]);
    if (first) {
      setFormErr("Please check the highlighted fields above.");
      setTimeout(() => {
        const el = document.querySelector(`[data-field="${first}"]`);
        if (!el) return;
        const y = el.getBoundingClientRect().top + window.scrollY - 160;
        if (window.__lenis) window.__lenis.scrollTo(y);
        else window.scrollTo({ top: y, behavior: "smooth" });
        el.focus({ preventScroll: true });
      }, 60);
    } else {
      setFormErr(null);
    }

    return Object.keys(er).length === 0;
  };

  // Razorpay's checkout script, loaded once, on demand.
  const loadRazorpay = () =>
    new Promise((resolve, reject) => {
      if (window.Razorpay) return resolve();
      const el = document.createElement("script");
      el.src = "https://checkout.razorpay.com/v1/checkout.js";
      el.onload = () => resolve();
      el.onerror = () => reject(new Error("Razorpay script failed to load"));
      document.body.appendChild(el);
    });

  const placeOrder = async () => {
    if (!validate()) return;
    setPayErr(null);
    setBusy(true);

    try {
      await loadRazorpay();

      // The browser sends product ids and quantities ONLY.
      // Every price comes back from the database, inside the function.
      const { data, error } = await supabase.functions.invoke("create-order", {
        body: {
          items: items.map((l) => ({ product_id: l.id, qty: l.qty })),
          contact: { email: form.email.trim().toLowerCase(), phone: form.mobile.trim() },
          address: {
            full_name: form.name.trim(),
            phone: form.addrMobile.trim(),
            line1: form.flat.trim(),
            line2: form.street.trim(),
            landmark: form.landmark.trim() || null,
            city: form.city.trim(),
            state: form.state.trim(),
            pincode: form.pincode.trim(),
          },
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const rzp = new window.Razorpay({
        key: data.key_id,
        order_id: data.rzp_order_id,
        amount: data.amount_paise,
        currency: "INR",
        name: "Ayushi by Dr. Shibina",
        description: `Order ${data.order_no}`,
        prefill: {
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          contact: form.mobile.trim(),
        },
        theme: { color: "#1B4A5F" },
        handler: () => {
          // The webhook is what actually marks the order paid.
          // This only tells the customer the payment went through.
          clear();
          setPlaced({ id: data.order_no, total: data.amount_paise / 100 });
          window.__lenis?.scrollTo(0, { immediate: true });
          window.scrollTo(0, 0);
        },
        modal: {
          ondismiss: () => setBusy(false),
        },
      });

      rzp.on("payment.failed", (resp) => {
        setBusy(false);
        setPayErr(resp?.error?.description || "The payment didn't go through. Please try again.");
      });

      rzp.open();
    } catch (e) {
      console.error("Checkout failed:", e);
      setBusy(false);
      setPayErr(
        e?.message === "Razorpay script failed to load"
          ? "We couldn't reach the payment provider. Check your connection and try again."
          : "Something went wrong starting the payment. Please try again, or WhatsApp us."
      );
    }
  };

  if (placed) {
    return (
      <div className="mx-auto flex max-w-2xl flex-col items-center px-4 pb-28 pt-44 text-center" data-testid="order-confirmation">
        <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="flex h-16 w-16 items-center justify-center rounded-full bg-sage-deep text-cream">
          <Check className="h-7 w-7" />
        </motion.div>
        <h1 className="mt-10 font-serif text-4xl font-light md:text-5xl">
          Thank you. <span className="italic text-sage-deep">Your order is in.</span>
        </h1>
        <p className="mt-6 max-w-md text-base leading-relaxed text-ink/70">
          Order <span className="font-medium text-ink" data-testid="order-id">{placed.id}</span> for{" "}
          {formatINR(placed.total)}. A confirmation goes to your email and WhatsApp. We pack orders
          Monday to Saturday, hand them to the courier within two working days, and delivery takes
          three to seven.
        </p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-5">
          <Link to="/account?tab=orders" data-testid="confirmation-track" className="rounded-full bg-ink px-8 py-4 text-[12px] uppercase tracking-[0.2em] text-cream transition-colors hover:bg-ink-deep">
            Track This Order
          </Link>
          <Link to="/shop" data-testid="confirmation-continue" className="border-b border-ink/30 pb-1 text-[12px] uppercase tracking-[0.2em] text-ink/70 transition-colors hover:border-ink hover:text-ink">
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center px-4 pb-28 pt-44 text-center">
        <Sprig className="h-14 w-auto text-sage-deep" />
        <h1 className="mt-8 font-serif text-4xl font-light italic">Your cart is empty.</h1>
        <p className="mt-4 max-w-sm text-sm text-ink/60">Ten products, made by a doctor in Thiruvananthapuram — have a look.</p>
        <Link to="/shop" data-testid="checkout-empty-shop-cta" className="mt-10 rounded-full bg-ink px-8 py-4 text-[13px] uppercase tracking-[0.2em] text-cream transition-colors hover:bg-ink-deep">
          Shop the Range
        </Link>
      </div>
    );
  }

  const est = new Date(Date.now() + 5 * 86400000).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="mx-auto max-w-7xl px-4 pb-24 pt-36 md:px-8 md:pt-44" data-testid="checkout-page">
      <Reveal>
        <p className="eyebrow">Checkout</p>
        <h1 className="mt-4 font-serif text-4xl font-light md:text-5xl">
          Four steps, <span className="italic text-sage-deep">one page.</span>
        </h1>
        {!user && (
          <p className="mt-4 text-sm text-ink/60">
            Checking out as a guest.{" "}
            <Link to="/account" className="underline underline-offset-4" data-testid="checkout-signin-link">Sign in with your email</Link>{" "}
            to save orders and addresses — no password needed.
          </p>
        )}
      </Reveal>

      <div className="mt-12 grid gap-14 lg:grid-cols-12">
        <div className="space-y-12 lg:col-span-7">
          <section data-testid="step-contact">
            <StepHead n={1} title="Contact" />
            <div className="mt-6 grid gap-6 sm:grid-cols-2">
              <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} placeholder="For confirmation and invoice" error={errors.email} />
              <Field label="Mobile number" name="mobile" value={form.mobile} onChange={onChange} placeholder="10-digit mobile" error={errors.mobile} />
            </div>
          </section>

          <section data-testid="step-address">
            <StepHead n={2} title="Delivery address" />
            <div className="mt-6 grid gap-6 sm:grid-cols-2">
              <Field label="Full name" name="name" value={form.name} onChange={onChange} placeholder="Who receives it" error={errors.name} />
              <Field label="Mobile" name="addrMobile" value={form.addrMobile} onChange={onChange} placeholder="For the courier" error={errors.addrMobile} />
              <Field label="Pincode" name="pincode" value={form.pincode} onChange={onChange} placeholder="6-digit pincode" error={errors.pincode} />
              <Field label="Flat, house, building" name="flat" value={form.flat} onChange={onChange} placeholder="Flat 4B, Jas Buildings" error={errors.flat} />
              <Field label="Street, area" name="street" value={form.street} onChange={onChange} placeholder="Chavadimukku, Sreekaryam" span error={errors.street} />
              <Field label="Landmark" name="landmark" value={form.landmark} onChange={onChange} placeholder="Near the junction" optional />
              <Field label="City" name="city" value={form.city} onChange={onChange} placeholder="Thiruvananthapuram" error={errors.city} />
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.22em] text-ink/55">State</span>
                <select
                  name="state"
                  value={form.state}
                  onChange={onChange}
                  data-testid="checkout-input-state"
                  data-field="state"
                  aria-invalid={errors.state ? "true" : "false"}
                  className={`mt-2 w-full border bg-white/70 px-4 py-3.5 text-[15px] text-ink transition-colors ${
                    errors.state ? "border-[#B3261E] bg-[#FFF6F5]" : "hairline"
                  }`}
                >
                  <option value="">Select a state</option>
                  {IN_STATES.map((st) => (
                    <option key={st} value={st}>{st}</option>
                  ))}
                </select>
                {errors.state && (
                  <p className="mt-2 text-[12px] font-medium text-[#B3261E]" data-testid="error-state">
                    {errors.state}
                  </p>
                )}
              </label>
            </div>
            <label className="mt-5 flex items-center gap-3 text-sm text-ink/65">
              <input type="checkbox" name="saveAddr" checked={form.saveAddr} onChange={onChange} data-testid="checkout-save-address" className="h-4 w-4 accent-[#1B4A5F]" />
              Save this address for next time
            </label>
          </section>

          <section data-testid="step-delivery">
            <StepHead n={3} title="Delivery" />
            <div className="mt-6 border hairline bg-white/60 p-6">
              <div className="flex items-center justify-between">
                <p className="text-[15px]">Standard delivery, 3 to 7 working days</p>
                <p className="font-medium tabular-nums" data-testid="delivery-charge">{delivery === 0 ? "Free" : formatINR(delivery)}</p>
              </div>
              <p className="mt-2 text-[13px] text-ink/55">
                Free on orders above ₹1,500. Estimated delivery around {est}.
              </p>
            </div>
          </section>

          <section data-testid="step-payment">
            <StepHead n={4} title="Payment" />
            <div className="mt-6 divide-y divide-ink/10 border hairline bg-white/60" data-testid="payment-methods">
              {PAYMENT_METHODS.map((m) => (
                <label key={m} className="flex cursor-pointer items-center gap-4 px-6 py-4 transition-colors hover:bg-cream/60">
                  <input type="radio" name="payment" checked={payment === m} onChange={() => setPayment(m)} data-testid={`payment-${m.toLowerCase().replace(/\s|\/|or/g, "-")}`} className="h-4 w-4 accent-[#1B4A5F]" />
                  <span className="text-[15px]">{m}</span>
                </label>
              ))}
            </div>
            <p className="mt-3 text-[12px] leading-relaxed text-ink/50">
              Payments are handled securely by Razorpay. You choose your method in the payment window.
            </p>
          </section>
        </div>

        <div className="lg:col-span-5">
          <div className="sticky top-40 border hairline bg-white/60 p-8" data-testid="checkout-summary">
            <h2 className="font-serif text-2xl italic">Your order</h2>
            <div className="mt-6 space-y-4 border-t hairline pt-6">
              {items.map((line) => {
                const item = resolve(line.id);
                if (!item) return null;
                return (
                  <div key={line.id} className="flex justify-between gap-4 text-sm">
                    <p className="text-ink/75">
                      {item.name} <span className="text-ink/40">× {line.qty}</span>
                    </p>
                    <p className="tabular-nums">{formatINR(item.price * line.qty)}</p>
                  </div>
                );
              })}
            </div>
            <dl className="mt-6 space-y-3 border-t hairline pt-6 text-sm">
              <div className="flex justify-between">
                <dt className="text-ink/60">Subtotal</dt>
                <dd className="tabular-nums">{formatINR(subtotal)}</dd>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-sage-deep">
                  <dt>Coupon {coupon?.code}</dt>
                  <dd className="tabular-nums">−{formatINR(discount)}</dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt className="text-ink/60">Delivery</dt>
                <dd className="tabular-nums">{delivery === 0 ? "Free" : formatINR(delivery)}</dd>
              </div>
              <div className="flex justify-between border-t hairline pt-4 font-serif text-xl">
                <dt>Total</dt>
                <dd className="tabular-nums" data-testid="checkout-total">{formatINR(total)}</dd>
              </div>
            </dl>
            <button onClick={placeOrder} disabled={busy} data-testid="place-order-button" className="mt-8 w-full rounded-full bg-ink px-8 py-4 text-[13px] uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-ink-deep disabled:opacity-60">
              {busy ? "Opening payment…" : `Place Order ${formatINR(total)}`}
            </button>
            {formErr && (
              <p className="mt-4 text-center text-[13px] font-medium text-[#B3261E]" data-testid="form-error">{formErr}</p>
            )}
            {payErr && (
              <p className="mt-4 text-center text-[13px] text-ink/70" data-testid="payment-error">{payErr}</p>
            )}
            <p className="mt-5 text-center text-[11px] leading-relaxed tracking-[0.06em] text-ink/45" data-testid="reassurance-strip">
              Secure payment · Delivered across India · Questions? WhatsApp {CLINIC.phone}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
